import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';
import { ThreeElements } from '@react-three/fiber';
import { BufferGeometry, ColorRepresentation } from 'three';

import { extractEdgeSegments } from '../extractEdgeSegments';
import { raycastBufferGeometry } from './raycastBufferGeometry';
import { ManySegment } from './Segments';
import { NEVER_RAYCAST } from './truss-envelopes/TrussEnvelopeVisual';

export type LayoutSolidVisualProps = ThreeElements['group'] & {
  geometry: BufferGeometry;

  wireframe?: boolean;
  dashed?: boolean;

  opacity?: number;

  edgeColor: ColorRepresentation;
  meshColor: ColorRepresentation;
};

export function LayoutSolidVisual({
  geometry,
  wireframe,
  dashed,
  opacity,
  edgeColor,
  meshColor,
  ...groupProps
}: LayoutSolidVisualProps) {
  const edgePositions = useMemo(() => extractEdgeSegments(geometry), [geometry]);
  const edgeSegments = useMemo(
    () => edgePositions.map((edge) => ({ ...edge, color: edgeColor })),
    [edgePositions, edgeColor],
  );

  const userData = useMemo(() => ({ raycastGeometry: geometry }), [geometry]);

  return (
    <group {...groupProps} userData={userData} raycast={raycastBufferGeometry}>
      {!wireframe && (
        <mesh geometry={geometry} raycast={NEVER_RAYCAST}>
          <meshBasicMaterial
            color={meshColor}
            transparent={opacity != null}
            opacity={opacity ?? 1}
            depthWrite={opacity == null}
            polygonOffset
            polygonOffsetFactor={1}
            polygonOffsetUnits={1}
          />
        </mesh>
      )}

      {dashed ? (
        edgePositions.map((edge, i) => (
          <Line
            key={i}
            dashed
            dashSize={3}
            gapSize={2}
            lineWidth={1}
            color={edgeColor}
            points={[edge.start, edge.end]}
          />
        ))
      ) : (
        <ManySegment segments={edgeSegments} />
      )}
    </group>
  );
}
